import {
  memo,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  createContext,
  useContext,
  useEffect,
} from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { PartialVLF } from 'components/Search/types';
import { useSavedSearch } from 'components/SavedSearch/SavedSearchContextProvider';
import { Editor } from '@tiptap/react';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';
import { HandleMediaLibraryInsertionArgs } from '../../collection/types';
import { useAssetConfigContext } from '../../../context/AssetConfigContextProvider';
import { parseSavedSearchToFilters } from './utils/savedSearchUtils';
import { shouldClearQuickFilter } from './utils/filterComparisonUtils';
import { normalizeFiltersForDataType } from 'components/Search/utility/filterUtils';
import { useRelationshipsInsert } from './hooks/useRelationshipsInsert';
import { useAddToRelationship } from './hooks/useAddToRelationship';
import type { RelationshipType } from './utils/relationshipUtils';
import { RelationshipsSearchModalConfig } from 'screens/editorial/asset/context/AssetViewContextProvider';

type RelationshipsSearchContextType = {
  filters: PartialVLF;
  debouncedFilters: PartialVLF;
  updateFilter: (key: string, value: any) => void;
  updateFilters: (filters: PartialVLF) => void;
  clearFilters: () => void;
  resetFilters: (preservedKeys?: string[]) => void;
  stagedFilters: PartialVLF;
  setStagedFilters: React.Dispatch<React.SetStateAction<PartialVLF>>;
  onInsert?: (items: EditorialAssetSearchResultItem[]) => void;
  hasInsertCapability: boolean;
  currentRecordUuid?: string; // UUID of the current asset being edited
  currentRecordType?: string; // Type of the current asset being edited (article, collection, etc.)
  hasAddToRelationshipCapability: boolean; // True when modal is for adding to relationships
  onCloseModal?: () => void; // Callback to close the modal
  isUseAssetMode: boolean; // True when modal is for selecting assets (e.g., FileFieldInput)
  relationshipType?: RelationshipType; // For relationship insertion mode
  footerActionMode?: RelationshipsSearchModalConfig['footerActionMode'];
  selectionMode?: RelationshipsSearchModalConfig['selectionMode'];
};

const RelationshipsSearchContext = createContext<
  RelationshipsSearchContextType | undefined
>(undefined);

export const useRelationshipsSearch = () => {
  const context = useContext(RelationshipsSearchContext);
  if (!context) {
    throw new Error(
      'useRelationshipsSearch must be used within RelationshipsSearchProvider',
    );
  }
  return context;
};

type RelationshipsSearchProviderProps = {
  children: ReactNode;
  contentEditor?: Editor | null;
  onInsertIntoCollection?: (args: HandleMediaLibraryInsertionArgs) => void;
  onClose?: () => void;
  skipDefaultSearch?: boolean;
  typeFilter?: RelationshipsSearchModalConfig['typeFilter'];
  replaceNodePosition?: RelationshipsSearchModalConfig['replaceNodePosition'];
  onUseAsset?: RelationshipsSearchModalConfig['onUseAsset'];
  relationshipType?: RelationshipsSearchModalConfig['relationshipType'];
  footerActionMode?: RelationshipsSearchModalConfig['footerActionMode'];
  selectionMode?: RelationshipsSearchModalConfig['selectionMode'];
};

const DEFAULT_FILTERS: PartialVLF = {
  sort: 'start_time',
  dir: 'DESC',
};

/**
 * Provides local-state search context for the relationships search modal.
 * Manages filters completely in local state with NO URL parameter synchronization.
 */
export const RelationshipsSearchProvider = memo(
  ({
    children,
    contentEditor,
    onInsertIntoCollection,
    onClose,
    typeFilter,
    replaceNodePosition,
    skipDefaultSearch = false,
    onUseAsset,
    relationshipType,
    footerActionMode,
    selectionMode,
  }: RelationshipsSearchProviderProps) => {
    // Get default saved search from context
    const { defaultSavedSearchState, updateQuickFilterValue, lastQuickFilterRef } =
      useSavedSearch();

    // Get current record from asset config context
    const { recordCopy } = useAssetConfigContext();

    // Use direct relationship insertion if relationshipType is provided
    const addToRelationship = useAddToRelationship({ onClose: onClose });

    // Use custom hook for insert logic (content insertion only)
    const {
      handleInsert: handleContentInsert,
      hasInsertCapability: hasContentInsertCapability,
    } = useRelationshipsInsert(
      contentEditor,
      onInsertIntoCollection,
      recordCopy?.uuid,
      replaceNodePosition,
      onClose,
    );

    // Determine which insert handler to use - memoized to avoid recreating on every render
    const handleInsert = useMemo(
      () =>
        relationshipType
          ? (items: EditorialAssetSearchResultItem[]) =>
              addToRelationship(items, relationshipType)
          : handleContentInsert,
      [relationshipType, addToRelationship, handleContentInsert],
    );

    const hasInsertCapability = relationshipType ? true : hasContentInsertCapability;

    // Create unified handler that prioritizes onUseAsset if provided
    const handleSelection = useCallback(
      async (items: EditorialAssetSearchResultItem[]) => {
        if (onUseAsset) {
          // Use asset mode (e.g., FileFieldInput selection)
          await onUseAsset(items);
          if (onClose) {
            onClose();
          }
          return;
        }

        // Standard insert mode (collection or inline)
        if (hasInsertCapability) {
          handleInsert(items);
        }
      },
      [onUseAsset, hasInsertCapability, handleInsert, onClose],
    );

    // Determine if we have any selection capability (insert or use)
    const hasSelectionCapability = hasInsertCapability || Boolean(onUseAsset);

    // Initialize filters with default search if available, otherwise use DEFAULT_FILTERS
    // This runs synchronously before first render to prevent double query
    const initialFilters = useMemo(() => {
      // If skipDefaultSearch is true (e.g., from inline placeholder), use only typeFilter
      if (skipDefaultSearch) {
        const baseFilters = { ...DEFAULT_FILTERS };
        if (typeFilter && typeFilter.length > 0) {
          baseFilters.type = typeFilter;
        }
        return baseFilters;
      }

      const hasDefaultSavedSearch = Boolean(
        defaultSavedSearchState?.initialized &&
        defaultSavedSearchState?.hasDefaultSavedSearch &&
        defaultSavedSearchState?.defaultSavedSearch,
      );

      if (!hasDefaultSavedSearch) {
        return DEFAULT_FILTERS;
      }

      const ssItem = defaultSavedSearchState?.defaultSavedSearch;
      const ssPayload = ssItem?.payload || {};

      // Parse the saved search into filters
      return parseSavedSearchToFilters(ssPayload, DEFAULT_FILTERS);
    }, [
      defaultSavedSearchState?.initialized,
      defaultSavedSearchState?.hasDefaultSavedSearch,
      defaultSavedSearchState?.defaultSavedSearch,
      skipDefaultSearch,
      typeFilter,
    ]);

    // Local state for search filters - initialized with default search if available
    const [filters, setFilters] = useState<PartialVLF>(initialFilters);

    // Staged filters for the advanced filters popover - persists across popover open/close
    const [stagedFilters, setStagedFilters] = useState<PartialVLF>(initialFilters);

    // Debounced filters for the actual search query
    const [debouncedFilters] = useDebouncedValue(filters, 400);

    // Track if we're in initial setup to prevent comparison from clearing quick filter
    const [isInitialSetup, setIsInitialSetup] = useState(true);

    // Set quick filter value and mark initial setup complete after mount
    useEffect(() => {
      const hasDefaultSavedSearch = Boolean(
        defaultSavedSearchState?.initialized &&
        defaultSavedSearchState?.hasDefaultSavedSearch &&
        defaultSavedSearchState?.defaultSavedSearch,
      );

      if (hasDefaultSavedSearch) {
        const ssItem = defaultSavedSearchState?.defaultSavedSearch;
        // Update quick filter value in saved search context
        updateQuickFilterValue(ssItem);
      }

      // Mark initial setup as complete after a brief delay to allow state to settle
      setTimeout(() => setIsInitialSetup(false), 100);
    }, [
      defaultSavedSearchState?.initialized,
      defaultSavedSearchState?.hasDefaultSavedSearch,
      defaultSavedSearchState?.defaultSavedSearch,
      updateQuickFilterValue,
    ]);

    // Clear quick filter selection when filters are manually changed
    useEffect(() => {
      // Don't check during initial setup
      if (isInitialSetup) {
        return;
      }

      // Check if filters have diverged from the last applied quick filter
      if (shouldClearQuickFilter(filters, lastQuickFilterRef)) {
        updateQuickFilterValue(null);
      }
    }, [filters, lastQuickFilterRef, updateQuickFilterValue, isInitialSetup]);

    // Update a single filter and keep stagedFilters in sync to avoid races between staged and applied state
    const updateFilter = useCallback((key: string, value: any) => {
      setFilters((prev) => {
        const next = { ...prev } as Partial<Record<string, any>>;

        if (
          value === '' ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0)
        ) {
          delete next[key];
        } else {
          next[key] = value;
        }

        return next as PartialVLF;
      });
    }, []);

    // Update filters in a batch, REPLACES all filters.
    const updateFilters = useCallback((newFilters: PartialVLF) => {
      const filters: PartialVLF = { ...newFilters };
      setFilters(filters);
      try {
        setStagedFilters(normalizeFiltersForDataType(filters));
      } catch (e) {
        setStagedFilters(filters);
      }
    }, []);

    // Clear ALL filters, no defaults.
    const clearFilters = useCallback(() => {
      const empty: PartialVLF = {};
      setFilters(empty);
      try {
        setStagedFilters(normalizeFiltersForDataType(empty));
      } catch (e) {
        setStagedFilters(empty);
      }
    }, []);

    // Reset filters preserving only preserved keys.
    const resetFilters = useCallback((preservedKeys: string[] = []) => {
      setFilters((prev) => {
        const preserved = preservedKeys.reduce((acc, key) => {
          if (prev[key] !== undefined) {
            acc[key] = prev[key];
          }
          return acc;
        }, {} as PartialVLF);

        const final: PartialVLF = { ...preserved };
        return final;
      });
    }, []);

    const contextValue = useMemo<RelationshipsSearchContextType>(
      () => ({
        filters,
        debouncedFilters,
        updateFilter,
        updateFilters,
        clearFilters,
        resetFilters,
        stagedFilters,
        setStagedFilters,
        onInsert: hasSelectionCapability ? handleSelection : undefined,
        hasInsertCapability: hasSelectionCapability,
        currentRecordUuid: recordCopy?.uuid,
        currentRecordType: recordCopy?.type,
        hasAddToRelationshipCapability: true, // Always true in modal context
        onCloseModal: onClose,
        isUseAssetMode: Boolean(onUseAsset), // True when onUseAsset handler is provided
        relationshipType,
        footerActionMode,
        selectionMode,
      }),
      [
        filters,
        debouncedFilters,
        updateFilter,
        updateFilters,
        clearFilters,
        resetFilters,
        stagedFilters,
        setStagedFilters,
        handleSelection,
        hasSelectionCapability,
        recordCopy?.uuid,
        recordCopy?.type,
        onClose,
        onUseAsset,
        relationshipType,
        footerActionMode,
        selectionMode,
      ],
    );

    // Wait for SavedSearchContext to initialize before rendering children
    // This prevents InfiniteListBase from mounting and querying with wrong filters
    if (!defaultSavedSearchState?.initialized) {
      return null;
    }

    return (
      <RelationshipsSearchContext.Provider value={contextValue}>
        {children}
      </RelationshipsSearchContext.Provider>
    );
  },
);

RelationshipsSearchProvider.displayName = 'RelationshipsSearchProvider';
